import { useState, type ReactNode } from 'react';
import { CornerDownRight, MessageCircleQuestion } from 'lucide-react';
import {
  answerFor,
  answersComplete,
  backlogAnswerNote,
  formatFactoryAnswers,
  plainOptionLabel,
  TEXT_MAX,
  formatQuestionsReply,
  QUESTIONS_REPLY_MAX,
  SKIPPED_ANSWER,
  type FactoryAnswer,
  type FactoryQuestion,
  type FactoryQuestions,
} from '@shared/factory-questions';
import { TASK_CHAT_MAX } from '@shared/types/tasks';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useTaskDraft } from './task-drafts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { DecisionItemInput } from './DecisionItemInput';
import { FoldingCard } from './FoldingCard';

/**
 * T-55: the software factory's questions on a task (a `factory-questions` block in its comment), as
 * a card, copied from the factory's own so they look and answer the same. While they're open, anyone
 * who can comment picks options or writes an answer and sends them as one comment (the factory's
 * Answers format), which the factory reads back. Once answered, or without `onAnswer`, it shows the
 * questions and their options, or with `answers` each question's answer. A plain comment doesn't
 * close it. Reply in words opens a box for one written reply in place of an answer per question
 * (formatQuestionsReply), which closes the round like Send answers; once answered, `words` shows it.
 * T-99: a grill-phase decisions block, with each question's detail, its recommended option, Skip (the
 * agent's recommendation) and Backlog (a task for later), and the round's `progress` line.
 */
export function FactoryQuestionsCard({
  questions,
  answered = false,
  answers: given,
  words,
  meta,
  from = 'the AI builder',
  fold = false,
  id,
  onAnswer,
}: {
  questions: FactoryQuestions;
  /** Who asked, in the header. */
  from?: string;
  /** A person has replied since (the timeline knows): the questions show as asked, not to answer. */
  answered?: boolean;
  /** Once answered: one answer per question, in order (parseFactoryAnswers), shown in place of the options. */
  answers?: (string | null)[] | null;
  /** Once answered with a reply in words, its text, shown after the questions. */
  words?: string | null;
  /** Who asked and when, at the end of the header line (the task's Decisions). */
  meta?: ReactNode;
  /** Starts folded to its header line, which opens it (an answered round in the task's Decisions). */
  fold?: boolean;
  /** For a pointer in the comments to scroll to it (decisionRoundId). */
  id?: string;
  /** Posts the answers (or a reply in words) as a comment; leave out where nobody can answer (a preview). */
  onAnswer?: (message: string) => void;
}) {
  const [typed, setAnswers] = useState<Record<string, FactoryAnswer>>({});
  // The Reply in words box; null while it's closed.
  const [reply, setReply] = useState<string | null>(null);
  const open = !answered && Boolean(onAnswer);
  const answers: Record<string, FactoryAnswer | undefined> = typed;
  const set = (id: string, change: Partial<FactoryAnswer>) =>
    setAnswers((prev) => {
      const was = answerFor(prev, id) ?? { picked: [], other: '' };
      return { ...prev, [id]: { ...was, ...change } };
    });
  const complete = answersComplete(questions.questions, answers);
  const message = formatFactoryAnswers(questions.questions, answers);
  // One comment holds them all: past the limit the server would cut it short, so it isn't sent.
  const tooLong = message.length > TASK_CHAT_MAX;
  // Picked or written and not sent: leaving the page or closing the task asks first.
  const dirty =
    open &&
    (reply?.trim() ? true : Object.values(typed).some((a) => Boolean(a && (a.skipped || a.backlog || a.picked.length || a.other.trim()))));
  useUnsavedChangesGuard(dirty, 'Your answers to the questions aren’t sent yet. Leave without sending them?');
  useTaskDraft(dirty);

  return (
    <FoldingCard
      id={id}
      fold={fold}
      summary={given ? answersSummary(questions.questions, given) : words ? `Replied: ${words.replace(/\s+/g, ' ')}` : undefined}
      header={
        <>
          <MessageCircleQuestion className="size-4" aria-hidden />
          {questions.questions.length === 1 ? `A question from ${from}` : `${questions.questions.length} questions from ${from}`}
          {answered ? <Badge variant="secondary">Answered</Badge> : null}
          {meta ? <span className="ml-auto font-normal">{meta}</span> : null}
        </>
      }
    >
      {questions.progress && !answered ? <p className="text-xs text-muted-foreground">{questions.progress}</p> : null}
      <ol className="space-y-3">
        {questions.questions.map((q, i) => (
          <li key={q.id} className="space-y-1.5">
            <p className="text-sm">
              <span className="text-muted-foreground">{i + 1}. </span>
              {q.header ? <span className="font-medium">{q.header}: </span> : null}
              {q.question}
            </p>
            {q.detail ? <p className="whitespace-pre-wrap break-words pl-4 text-xs text-muted-foreground">{q.detail}</p> : null}
            {open && reply === null ? (
              <QuestionInput question={q} answer={answerFor(answers, q.id)} onChange={(change) => set(q.id, change)} />
            ) : given ? (
              <GivenAnswer answer={given[i] ?? null} />
            ) : (
              <OptionList question={q} />
            )}
          </li>
        ))}
      </ol>
      {words && !given ? (
        <div className="space-y-1 border-t pt-2">
          <p className="text-xs text-muted-foreground">Replied in words</p>
          <GivenAnswer answer={words} />
        </div>
      ) : null}
      {open && reply !== null ? (
        <div className="space-y-2">
          <Textarea
            aria-label="Your reply to the questions"
            placeholder="Answer or skip the questions in your own words…"
            rows={3}
            maxLength={QUESTIONS_REPLY_MAX}
            autoFocus
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setReply(null)}>
              Back to the questions
            </Button>
            <Button size="sm" disabled={!reply.trim()} onClick={() => onAnswer?.(formatQuestionsReply(reply))}>
              Send reply
            </Button>
          </div>
        </div>
      ) : open ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setReply('')}>
            Reply in words
          </Button>
          {!complete ? (
            <span className="text-xs text-muted-foreground">Answer, skip or backlog each question to send</span>
          ) : tooLong ? (
            <span className="text-xs text-destructive">Too long for one comment: shorten your answers</span>
          ) : null}
          <Button size="sm" disabled={!complete || tooLong} onClick={() => onAnswer?.(message)}>
            Send answers
          </Button>
        </div>
      ) : null}
    </FoldingCard>
  );
}

/** The answers on one line for a folded round: "Scope: Only tasks it built · Where: On the task". */
function answersSummary(list: FactoryQuestion[], given: (string | null)[]): string | undefined {
  const parts = list.flatMap((q, i) => {
    const answer = answerText(given[i] ?? null)?.replace(/\s*\(Recommended\)/g, '').replace(/\s+/g, ' ').trim();
    return answer ? [q.header ? `${q.header}: ${answer}` : answer] : [];
  });
  return parts.length ? parts.join(' · ') : undefined;
}

/** T-99: an answer as it reads on the card: Skipped and Backlog (with its note) in words, not their markers. */
function answerText(answer: string | null): string | null {
  if (answer === SKIPPED_ANSWER) return 'Skipped';
  const note = backlogAnswerNote(answer);
  if (note !== null) return note ? `Backlog: ${note}` : 'Backlog';
  return answer;
}

function GivenAnswer({ answer }: { answer: string | null }) {
  const text = answerText(answer);
  const plain = answer === SKIPPED_ANSWER || backlogAnswerNote(answer) !== null;
  return (
    <p className={cn('flex items-start gap-1.5 pl-4 text-sm', text && !plain ? 'font-medium' : 'text-muted-foreground')}>
      <CornerDownRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 whitespace-pre-line break-words">{text ?? 'No answer'}</span>
    </p>
  );
}

/**
 * In the comments, the factory's questions as one line pointing to the task's Decisions (under the
 * description), where they're answered and kept with their answers.
 */
export function FactoryQuestionsPointer({
  count,
  answered = false,
  onShow,
}: {
  count: number;
  answered?: boolean;
  /** Scrolls to the questions (showFoldingCard). */
  onShow: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
      <MessageCircleQuestion className="size-4 shrink-0" aria-hidden />
      <span>{count === 1 ? 'Asked a question' : `Asked ${count} questions`}</span>
      {answered ? <Badge variant="secondary">Answered</Badge> : null}
      <Button variant="link" size="inline" onClick={onShow}>
        {answered ? 'See Decisions' : 'Answer under the description'}
      </Button>
    </div>
  );
}

function OptionList({ question }: { question: FactoryQuestion }) {
  if (!question.options.length) return null;
  return (
    <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground marker:text-muted-foreground">
      {question.options.map((o) => (
        <li key={o.label}>
          <span className="text-foreground">{plainOptionLabel(o.label)}</span>
          {o.label === question.recommended ? ' (recommended)' : null}
          {o.description ? ` — ${o.description}` : null}
        </li>
      ))}
    </ul>
  );
}

/** T-99: a question's controls, as every decision has them (DecisionItemInput), with Skip and Backlog. */
function QuestionInput({
  question,
  answer,
  onChange,
}: {
  question: FactoryQuestion;
  answer: FactoryAnswer | undefined;
  onChange: (change: Partial<FactoryAnswer>) => void;
}) {
  const choice = answer?.skipped ? 'skip' : answer?.backlog ? 'backlog' : null;
  return (
    <DecisionItemInput
      label={question.header ?? question.question}
      options={question.options}
      multiSelect={question.multiSelect}
      recommended={question.recommended}
      value={answer ? { picked: answer.picked, other: answer.other, choice } : undefined}
      maxLength={TEXT_MAX}
      actions={{ skip: true, backlog: true }}
      hints={{ skip: 'The AI builder goes with its recommendation', backlog: 'Left out of this build: the AI builder makes a task of it for later' }}
      onChange={(change) =>
        onChange({
          ...(change.picked !== undefined ? { picked: change.picked } : {}),
          ...(change.other !== undefined ? { other: change.other } : {}),
          ...(change.choice !== undefined ? { skipped: change.choice === 'skip', backlog: change.choice === 'backlog' } : {}),
        })
      }
    />
  );
}
